/**
 * `FormInfoHelpModal` copy for Rack Power rack cards (`RackPowerRackCards`, rack step).
 * Primary guidance is the first bullet (long descriptive sentence); following bullets add short context.
 */
import { RACK_POWER_FIELD_LABELS } from './rackPowerFieldLabels';
export const RACK_SUBSECTIONS_RACK_INFO_KEYS = [
  'rackPlatform',
  'rackQuantity',
  'rackDescription',
  'rackUse',
  'rackSeismicRated',
  'rackRequiresLiquidToChip',
  'rackMaxLoadRating',
  'rackColor',
  'rackDimensionsLength',
  'rackDimensionsHeight',
  'rackDimensionsWidth',
  'rackDescribeElevations',
  'rackAdditionalRackInformation',
] as const;
export type RackSubsectionsRackInfoKey =
  (typeof RACK_SUBSECTIONS_RACK_INFO_KEYS)[number];

export const RACK_SUBSECTIONS_RACK_FIELD_INFO: Record<
  RackSubsectionsRackInfoKey,
  { title: string; bullets: readonly string[] }
> = {
  rackPlatform: {
    title: 'Rack platform',
    bullets: [
      'Specifies rack form factor and mounting standard so height units (RU vs OU) and accessories align—choose Standard, OCP/ORV3, or MGX as applicable.',
    ],
  },
  rackQuantity: {
    title: 'Rack quantity',
    bullets: [
      'Quantity of racks that share this specific configuration (dimensions, power, and options described in this row), distinct from a facility-wide total rack count.',
    ],
  },
  rackDescription: {
    title: 'Description',
    bullets: [
      'Free-text description of this rack configuration (layout, constraints, or other notes that help engineering understand what is being scoped).',
    ],
  },
  rackUse: {
    title: 'Rack use',
    bullets: [
      'Describes the primary equipment type or role hosted in the rack (for example Server, Network, or Storage) so capacity and power assumptions align with the intended workload.',
      'Placeholder examples: Server, Network.',
    ],
  },
  rackSeismicRated: {
    title: 'Seismic rated?',
    bullets: [
      'Indicates whether the cabinet is rated for seismic anchoring or seismic load requirements for the installation region.',
    ],
  },
  rackRequiresLiquidToChip: {
    title: 'Liquid to chip cooling',
    bullets: [
      'Select whether direct liquid-to-chip or similar close-coupled liquid cooling is required for the equipment planned in this rack configuration.',
    ],
  },
  rackMaxLoadRating: {
    title: 'Max load rating',
    bullets: [
      'Static load limit the rack structure is designed to support (manufacturer rating); pick the closest standard rating or Other when the value does not match a list entry.',
    ],
  },
  rackColor: {
    title: 'Color',
    bullets: [
      'Cabinet finish color when it matters for facility standards, identification, or customer preference.',
    ],
  },
  rackDimensionsLength: {
    title: 'Depth',
    bullets: [
      'Overall rack depth, including any door or bumper allowance. Enter the number in inches. The chip on the field can show millimeters.',
    ],
  },
  rackDimensionsHeight: {
    title: 'Height [RU/OU]',
    bullets: [
      'Enter rack height in rack units (RU) for standard/MGX cabinets, or open units (OU) for OCP/ORV3—match the platform you selected.',
    ],
  },
  rackDimensionsWidth: {
    title: 'Width',
    bullets: [
      'Overall rack width. Enter the number in inches. The chip on the field can show millimeters.',
    ],
  },
  rackDescribeElevations: {
    title: 'Describe rack elevations',
    bullets: [
      'Describe how equipment is arranged by elevation (for example model counts per U position or per rack) so installers and power teams can mirror the intended layout.',
      'Example placeholder style: Dell XE9680 ×10, 2 per rack.',
    ],
  },
  rackAdditionalRackInformation: {
    title: 'Additional Rack Configuration Information',
    bullets: [
      'Any additional rack configuration notes, exceptions, or follow-up items that do not fit structured fields above.',
    ],
  },
} as const;

export function rackSubsectionsRackInfoA11y(key: RackSubsectionsRackInfoKey): {
  description: string;
  hint: string;
} {
  const p = RACK_SUBSECTIONS_RACK_FIELD_INFO[key];
  return {
    description: `${p.title}. ${p.bullets.join(' ')}`,
    hint: 'Opens help in a dialog.',
  };
}

/** Rack Power Information card (create + detail). */
export const RACK_POWER_CARD_INFO_KEYS = [
  'rackPduManufacturer',
  'rackPduModel',
  'rackPduMaxKwPerRack',
  'powerAverageItLoadPerRackKw',
  'powerMaximumItLoadPerRackKw',
  'powerNumberAndTypeInputPerRack',
  'rackPduPowerType',
  'rackPduInputPlugType',
  'rackPduCountPerRack',
  'rackPduDcPowerShelves',
  'rackPduOutletSwitchingMonitored',
  'rackPduCapacityAmps',
  'rackPduCapacityVolts',
  'rackPduCapacityKw',
  'rackPduLoadReadingsKw',
  'rackPduMountingType',
  'rackPduOutletTypesQuantity',
  'rackPduLocation',
  'rackPduAdditionalInfo',
] as const;
export type RackPowerCardInfoKey = (typeof RACK_POWER_CARD_INFO_KEYS)[number];

export const RACK_POWER_CARD_FIELD_INFO: Record<
  RackPowerCardInfoKey,
  { title: string; bullets: readonly string[] }
> = {
  rackPduManufacturer: {
    title: 'Manufacturer',
    bullets: [
      'PDU or rack power distribution manufacturer for this configuration (repeat per PDU model line when multiple types are used).',
    ],
  },
  rackPduModel: {
    title: 'Model',
    bullets: [
      'PDU model designation so ordering, accessories, and electrical characteristics can be checked against the site design.',
    ],
  },
  rackPduMaxKwPerRack: {
    title: 'Max kW per rack',
    bullets: [
      'Maximum design or nameplate kW intended for the rack; may be a single value or short range describing headroom versus average load.',
    ],
  },
  powerAverageItLoadPerRackKw: {
    title: 'Average IT load per rack',
    bullets: [
      'Typical operating IT load per rack in kilowatts, used to size feeds and understand steady-state power—enter a numeric kW value.',
    ],
  },
  powerMaximumItLoadPerRackKw: {
    title: 'Maximum IT load per rack',
    bullets: [
      'Peak or worst-case IT load per rack in kilowatts so breaker, cable, and PDU sizing cover transient or burst conditions.',
    ],
  },
  powerNumberAndTypeInputPerRack: {
    title: 'Number and type of PDUs',
    bullets: [
      'Describe PDU quantity and types for this rack configuration so rack power matches facility design.',
    ],
  },
  rackPduPowerType: {
    title: 'AC or DC power',
    bullets: [
      'Select all that apply when racks use AC rPDUs, DC power shelves, or a mix so the correct distribution and safety accessories are planned.',
    ],
  },
  rackPduInputPlugType: {
    title: 'Input plug type',
    bullets: [
      'PDU inlet or facility whip connector family (for example IEC C20, CS8365, or regional equivalents) so mating cords and breakers are specified correctly.',
    ],
  },
  rackPduCountPerRack: {
    title: 'How many rPDU per rack',
    bullets: [
      'Count of rack PDUs installed or required per rack for A/B side feeds, redundancy, or segmented loads.',
    ],
  },
  rackPduDcPowerShelves: {
    title: 'How many DC power shelves',
    bullets: [
      'Number of DC power shelf units per rack when DC distribution is used alongside or instead of AC rPDUs.',
    ],
  },
  rackPduOutletSwitchingMonitored: {
    title: RACK_POWER_FIELD_LABELS.rackPduOutletSwitchingMonitored,
    bullets: [
      'PDU feature level: unit-level monitoring, per-outlet monitoring, or switched outlets—pick the option that matches the required telemetry and remote control.',
    ],
  },
  rackPduCapacityAmps: {
    title: RACK_POWER_FIELD_LABELS.rackPduCapacityAmps,
    bullets: [
      'Rated or configured ampacity for the PDU feed or branch circuit relevant to this rack.',
    ],
  },
  rackPduCapacityVolts: {
    title: RACK_POWER_FIELD_LABELS.rackPduCapacityVolts,
    bullets: [
      'Nominal voltage class for the PDU input or shelf feed (for example 208 V, 400 V, or 48 V DC) as configured at the rack.',
    ],
  },
  rackPduCapacityKw: {
    title: RACK_POWER_FIELD_LABELS.rackPduCapacityKw,
    bullets: [
      'Thermal or electrical kW capacity associated with the PDU or shelf configuration for this rack.',
    ],
  },
  rackPduLoadReadingsKw: {
    title: RACK_POWER_FIELD_LABELS.rackPduLoadReadingsKw,
    bullets: [
      'Observed or reported total load in kilowatts for the rack PDUs when available from metering or BMS—helps validate design versus actual draw.',
    ],
  },
  rackPduMountingType: {
    title: RACK_POWER_FIELD_LABELS.rackPduMountingType,
    bullets: [
      'Whether PDUs mount vertically in the rack frame or horizontally (for example 0 U or shelf) for clearance and cable routing.',
    ],
  },
  rackPduOutletTypesQuantity: {
    title: RACK_POWER_FIELD_LABELS.rackPduOutletTypesQuantity,
    bullets: [
      'List each outlet type (for example C13/C19) and quantity required per PDU or per rack so cord sets and IT equipment plugs can be matched.',
    ],
  },
  rackPduLocation: {
    title: RACK_POWER_FIELD_LABELS.rackPduLocation,
    bullets: [
      'Where the PDU or shelf is mounted by U position or side (for example left vertical rear) so installers place devices consistently.',
    ],
  },
  rackPduAdditionalInfo: {
    title: 'Additional PDU Information',
    bullets: [
      'Any additional rack power notes, metering, maintenance windows, or exceptions not captured in structured fields.',
    ],
  },
} as const;

export function rackPowerCardInfoA11y(key: RackPowerCardInfoKey): {
  description: string;
  hint: string;
} {
  const p = RACK_POWER_CARD_FIELD_INFO[key];
  return {
    description: `${p.title}. ${p.bullets.join(' ')}`,
    hint: 'Opens help in a dialog.',
  };
}
